export function formatDdm(latitude: number, longitude: number): string {
  return `${formatDdmValue(latitude, "N", "S")} ${formatDdmValue(longitude, "E", "W")}`;
}

export function formatUtm(latitude: number, longitude: number): string {
  const zone = Math.floor((longitude + 180) / 6) + 1;
  const hemisphere = latitude >= 0 ? "N" : "S";
  const { easting, northing } = latLonToUtm(latitude, longitude, zone);
  return `${zone}${hemisphere} ${Math.round(easting)} ${Math.round(northing)}`;
}

function formatDdmValue(value: number, positive: string, negative: string): string {
  const direction = value >= 0 ? positive : negative;
  const absolute = Math.abs(value);
  const degrees = Math.floor(absolute);
  const minutes = (absolute - degrees) * 60;
  return `${degrees}° ${minutes.toFixed(5)}' ${direction}`;
}

function latLonToUtm(latitude: number, longitude: number, zone: number) {
  const a = 6378137.0;
  const f = 1 / 298.257223563;
  const k0 = 0.9996;
  const e = Math.sqrt(f * (2 - f));
  const ePrimeSquared = e * e / (1 - e * e);
  const latRad = toRadians(latitude);
  const lonRad = toRadians(longitude);
  const lonOrigin = toRadians((zone - 1) * 6 - 180 + 3);

  const n = a / Math.sqrt(1 - e * e * Math.sin(latRad) ** 2);
  const t = Math.tan(latRad) ** 2;
  const c = ePrimeSquared * Math.cos(latRad) ** 2;
  const aa = Math.cos(latRad) * (lonRad - lonOrigin);
  const m = a * (
    (1 - e * e / 4 - 3 * e ** 4 / 64 - 5 * e ** 6 / 256) * latRad
    - (3 * e * e / 8 + 3 * e ** 4 / 32 + 45 * e ** 6 / 1024) * Math.sin(2 * latRad)
    + (15 * e ** 4 / 256 + 45 * e ** 6 / 1024) * Math.sin(4 * latRad)
    - (35 * e ** 6 / 3072) * Math.sin(6 * latRad)
  );

  const easting = k0 * n * (
    aa
    + (1 - t + c) * aa ** 3 / 6
    + (5 - 18 * t + t * t + 72 * c - 58 * ePrimeSquared) * aa ** 5 / 120
  ) + 500000;

  let northing = k0 * (
    m + n * Math.tan(latRad) * (
      aa * aa / 2
      + (5 - t + 9 * c + 4 * c * c) * aa ** 4 / 24
      + (61 - 58 * t + t * t + 600 * c - 330 * ePrimeSquared) * aa ** 6 / 720
    )
  );

  if (latitude < 0) northing += 10000000;
  return { easting, northing };
}

function toRadians(value: number): number {
  return value * Math.PI / 180;
}
